import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Textarea,
  Text,
  VStack,
  IconButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Spinner,
  useToast,
  Link,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverCloseButton,
  Wrap,
  useBreakpointValue,
} from '@chakra-ui/react';
import { MoonIcon, SunIcon, CloseIcon } from '@chakra-ui/icons';
import ComponentFactory from '../Components/Factory/ComponentFactory';
import BackgroundWrapper from '../Components/BackgroundWrapper';
import ModalComponent from '../Components/ModalComponent';
import NavBarComponent from '../Components/NavBarComponent';
import ButtonComponent from '../Components/ButtonComponent';
import PageViewTracker from '../Components/PageViewTracker';
import appConfig from '../Config/appConfig';
import api from '../Api/api';
import InteractiveMermaid from '../Components/InteractiveMermaid';
import CustomTreeView from '../Components/CustomTreeView';

const Template = () => {
  const [jsonInput, setJsonInput] = useState('');
  const [parsedContent, setParsedContent] = useState(null);
  const [mermaidCode, setMermaidCode] = useState('');
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({});
  const priorDecisions = [];
  const [liveViewTheme, setLiveViewTheme] = useState('light');
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [isMouseDevice, setIsMouseDevice] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [modalContent, setModalContent] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [programPrefix, setProgramPrefix] = useState('');
  const [taskType, setTaskType] = useState('');
  const [revision, setRevision] = useState('1.0');
  const [isSaving, setIsSaving] = useState(false);
  const toast = useToast();

  // Responsive breakpoints for popover sizing
  const popoverWidth = useBreakpointValue({ base: "95vw", md: "80vw", lg: "70vw" });
  const popoverHeight = useBreakpointValue({ base: "90vh", md: "85vh", lg: "80vh" });

  useEffect(() => {
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const hasMouse = 'onmousemove' in window;
    setIsTouchDevice(hasTouch);
    setIsMouseDevice(hasMouse);
  }, []);

  const generateMermaidCode = (jsonStructure) => {
    if (!jsonStructure) {
      return 'graph TD\n  Start[Start] --> End[No content]';
    }
  
     const pages = Array.isArray(jsonStructure) ? jsonStructure : [jsonStructure];
     let mermaidString = 'graph TD\n';
     let stepCounter = 0;
     let decisionCounter = 0;
     let mergeCounter = 0;
     let sequentialCounter = 0;
     let lastNodeId = 'Start';
     
     // Start with the first node
     lastNodeId = 'Start';
  
    try {
      // Process each major section
      pages.forEach((item, index) => {
        const currentPrefix = `${index + 1}`;
        
        if (Array.isArray(item) && item.length > 1) {
          // This is a branch with multiple options
          decisionCounter++;
          const decisionId = `D${decisionCounter}`;
          
          // Connect to previous node
          mermaidString += `  ${lastNodeId} --> ${decisionId}{Branch ${currentPrefix}?};\n\n`;
          
          // Create subgraph for this branch
          mermaidString += `  subgraph Branch_${currentPrefix}\n`;
          mermaidString += `    direction LR\n`;
          
          const branchEnds = [];
          
          item.forEach((option, optionIndex) => {
            const subPrefix = `${currentPrefix}.${optionIndex + 1}`;
            
            if (Array.isArray(option) && option.length > 1) {
              // Nested branch with sequential steps (like 2.1, 2.2, 2.3, 2.4)
              stepCounter++;
              let stepId = `Step${stepCounter}`;
              const pageTitle = option[0].title || `step ${subPrefix}.1`;
              mermaidString += `    ${decisionId} -- Option ${subPrefix} --> ${stepId}[${pageTitle}];\n`;
              
              // Add sequential steps if there are more than 1
              if (option.length > 1) {
                for (let i = 1; i < option.length; i++) {
                  stepCounter++;
                  const nextStepId = `Step${stepCounter}`;
                  const nextPageTitle = option[i].title || `step ${subPrefix}.${i + 1}`;
                  mermaidString += `    ${stepId} --> ${nextStepId}[${nextPageTitle}];\n`;
                  stepId = nextStepId;
                }
              }
              
              branchEnds.push(stepId);
              
            } else if (Array.isArray(option) && option.length === 1) {
              // Single item array - check if it's a sequential path
              const singleItem = option[0];
              if (Array.isArray(singleItem)) {
                // Sequential path (like 14.1)
                sequentialCounter++;
                mermaidString += `    subgraph Sequential_${subPrefix.replace(/\./g, '_')}\n`;
                
                const sequentialSteps = [];
                singleItem.forEach((seqItem, seqIndex) => {
                  const seqNodeId = `S${sequentialCounter}_${seqIndex + 1}`;
                  const pageTitle = seqItem.title || `step ${subPrefix}.${seqIndex + 1}`;
                  sequentialSteps.push(`${seqNodeId}[${pageTitle}]`);
                });
                
                mermaidString += `      ${sequentialSteps.join(' --> ')};\n`;
                mermaidString += `    end\n`;
                
                // Connect decision to first step of sequential path
                mermaidString += `    ${decisionId} -- Option ${subPrefix} --> ${sequentialSteps[0]};\n`;
                
                branchEnds.push(sequentialSteps[sequentialSteps.length - 1]);
              } else {
                // Single object
                stepCounter++;
                const stepId = `Step${stepCounter}`;
                const pageTitle = singleItem.title || `step ${subPrefix}`;
                mermaidString += `    ${decisionId} -- Option ${subPrefix} --> ${stepId}[${pageTitle}];\n`;
                branchEnds.push(stepId);
              }
            } else {
              // Single object
              stepCounter++;
              const stepId = `Step${stepCounter}`;
              const pageTitle = option.title || `step ${subPrefix}`;
              mermaidString += `    ${decisionId} -- Option ${subPrefix} --> ${stepId}[${pageTitle}];\n`;
              branchEnds.push(stepId);
            }
          });
          
          mermaidString += `  end\n\n`;
          
          // Create merge for this branch if multiple options
          if (branchEnds.length > 1) {
            mergeCounter++;
            const mergeId = `Merge${mergeCounter}`;
            mermaidString += `  ${mergeId}[Merge]\n`;
            branchEnds.forEach(end => {
              mermaidString += `  ${end} --> ${mergeId};\n`;
            });
            mermaidString += '\n';
            lastNodeId = mergeId;
          } else {
            lastNodeId = branchEnds[0];
          }
          
        } else if (Array.isArray(item) && item.length === 1) {
          // Single item array
          const singleItem = item[0];
          if (Array.isArray(singleItem)) {
            // Sequential path
            sequentialCounter++;
            mermaidString += `  subgraph Sequential_${currentPrefix}\n`;
            
            const sequentialSteps = [];
            singleItem.forEach((seqItem, seqIndex) => {
              const seqNodeId = `S${sequentialCounter}_${seqIndex + 1}`;
              const pageTitle = seqItem.title || `step ${currentPrefix}.${seqIndex + 1}`;
              sequentialSteps.push(`${seqNodeId}[${pageTitle}]`);
            });
            
            mermaidString += `    ${sequentialSteps.join(' --> ')};\n`;
            mermaidString += `  end\n\n`;
            
            // Connect previous node to first step of sequential path
            mermaidString += `  ${lastNodeId} --> ${sequentialSteps[0]};\n\n`;
            
            lastNodeId = sequentialSteps[sequentialSteps.length - 1];
          } else {
            // Single object
            stepCounter++;
            const stepId = `Step${stepCounter}`;
            const pageTitle = singleItem.title || `step ${currentPrefix}`;
            mermaidString += `  ${lastNodeId} --> ${stepId}[${pageTitle}];\n\n`;
            lastNodeId = stepId;
          }
          
        } else if (item && typeof item === 'object') {
          // Single object
          stepCounter++;
          const stepId = `Step${stepCounter}`;
          const pageTitle = item.title || `step ${currentPrefix}`;
          mermaidString += `  ${lastNodeId} --> ${stepId}[${pageTitle}];\n\n`;
          lastNodeId = stepId;
        }
      });
      
             // Connect to end
       mermaidString += `  ${lastNodeId} --> End[End];\n`;
      
      return mermaidString;
    } catch (error) {
      console.error('Error in generateMermaidCode:', error);
      console.error('Error stack:', error.stack);
      throw error;
    }
  };

  const handleJsonChange = (value) => {
    setJsonInput(value);
    try {
      const parsed = JSON.parse(value);
      
      let contentToRender = null;
      let screensToRender = [];

      if (Array.isArray(parsed)) {
        contentToRender = parsed;
        // Extract individual screens from the nested structure
        screensToRender = extractScreensFromStructure(parsed);
      } else if (parsed && parsed.data && Array.isArray(parsed.data.StepsJson)) {
        contentToRender = parsed.data.StepsJson;
        screensToRender = extractScreensFromStructure(parsed.data.StepsJson);
      } else if (parsed && parsed.sections) {
        contentToRender = [parsed];
        screensToRender = [parsed];
      } else {
        throw new Error('Unsupported JSON structure');
      }


      setParsedContent(screensToRender);
      setError('');
      setMermaidCode(generateMermaidCode(contentToRender));
    } catch (err) {
      console.error('Error in handleJsonChange:', err);
      setError(`Error: ${err.message}`);
      setParsedContent(null);
      setMermaidCode('');
    }
  };

  // Helper function to convert parsed content into tree structure
  const convertToTreeStructure = (screens) => {
    if (!screens || screens.length === 0) return null;
    
    const treeData = {
      id: 'root',
      label: 'All Screens',
      children: []
    };
    
    // Group screens by their prefix levels
    const groupedScreens = {};
    
    screens.forEach(screen => {
      const prefix = screen._prefix;
      if (!prefix) return;
      
      const parts = prefix.split('.');
      let currentPath = '';
      
      parts.forEach((part, index) => {
        const parentPath = currentPath;
        currentPath = currentPath ? `${currentPath}.${part}` : part;
        
        if (!groupedScreens[currentPath]) {
          groupedScreens[currentPath] = {
            id: currentPath,
            label: `Screen ${currentPath}`,
            children: [],
            screen: null
          };
        }
        
        // If this is a leaf node (actual screen), store the screen data
        if (index === parts.length - 1) {
          groupedScreens[currentPath].screen = screen;
          groupedScreens[currentPath].label = screen.title || `Screen ${currentPath}`;
        }
        
        // Add to parent's children if not already there
        if (parentPath && groupedScreens[parentPath]) {
          const exists = groupedScreens[parentPath].children.find(child => child.id === currentPath);
          if (!exists) {
            groupedScreens[parentPath].children.push(groupedScreens[currentPath]);
          }
        }
      });
    });
    
    // Find root level items (those without parents)
    Object.values(groupedScreens).forEach(item => {
      const hasParent = Object.values(groupedScreens).some(parent => 
        parent.children.some(child => child.id === item.id)
      );
      
      if (!hasParent) {
        treeData.children.push(item);
      }
    });
    
    return treeData;
  };

  // Helper function to extract individual screens from nested structure
  const extractScreensFromStructure = (structure) => {
    const screens = [];
    let screenCount = 0;
    
    const processItem = (item, prefix = '') => {
      if (Array.isArray(item)) {
        // Array represents decision branches - each item in array is a separate screen
        item.forEach((subItem, index) => {
          const currentPrefix = prefix ? `${prefix}.${index + 1}` : `${index + 1}`;
          processItem(subItem, currentPrefix);
        });
      } else if (item && typeof item === 'object') {
        // Single object - this is a screen
        screenCount++;
        const screenWithPrefix = {
          ...item,
          _prefix: prefix,
          title: item.title || `Screen ${prefix}`
        };
        screens.push(screenWithPrefix);
      }
    };
    
    structure.forEach((item, index) => {
      const prefix = `${index + 1}`;
      processItem(item, prefix);
    });
    
    return screens;
  };

  const updateFormData = (id, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [id]: { value: value || "" },
    }));
  };

  const handleSave = async () => {
    if (error || !parsedContent) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the JSON input and provide valid content before saving.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    
    setIsSaving(true);

    try {
      const endpoint = `${appConfig.APIBaseURL}/api/v1/content`;
      const minifiedJson = JSON.stringify(JSON.parse(jsonInput)); 

      const payload = {
        ProgramPrefix: programPrefix,
        TaskType: taskType,
        Revision: revision,
        JsonData: minifiedJson,
      };

      const response = await api.post(endpoint, payload);

      if (response.status === 200) {
        setProgramPrefix('');
        setTaskType('');
        setRevision('1.0');
        setJsonInput('');
        setParsedContent(null);
        setFormData({});
        
        toast({
          title: 'Success!',
          description: response.data.message || 'Data saved successfully.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        throw new Error('Unexpected response status: ' + response.status);
      }
    } catch (apiError) {
      console.error('Failed to save data:', apiError);
      toast({
        title: 'Error Saving Data',
        description: apiError.message || 'There was an issue calling the API. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const renderContent = (item) => {
    if (!item) {
      return <Text color="gray.500">No content to render</Text>;
    }

    // Create a proper page wrapper that mimics the actual app layout
    return (
      <Box
        width="100%"
        height="100%"
        display="flex"
        flexDirection="column"
        bg="white"
        borderRadius="md"
        overflow="hidden"
        boxShadow="lg"
      >
        <BackgroundWrapper
          sections={item.sections}
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Flex direction="column" height="100%">
            {/* Navbar */}
            {item.sections?.find(s => s.type === 'navbar') && (
              <NavBarComponent
                image={item.sections.find(s => s.type === 'navbar').image}
                buttons={item.sections.find(s => s.type === 'navbar').button}
              />
            )}
            
            {/* Main Content Area */}
            <Box 
              flex="1" 
              p={4} 
              display="flex" 
              flexDirection="column"
              overflowY="auto"
              overflowX="hidden"
            >
              {item.sections?.map((section, sectionIndex) => {
                if (section.type === 'navbar') return null;
                
                if (section.type === 'button' && Array.isArray(section.cta)) {
                  const hasHalfVariant = section.cta.some(ctaItem => ctaItem.variant?.includes('-half'));
                  const Wrapper = hasHalfVariant ? Flex : Box;
                  const wrapperProps = hasHalfVariant ? { 
                    justify: "space-between", 
                    gap: 4, 
                    width: "100%",
                    mt: "auto", // Push buttons to bottom
                    pt: 4
                  } : { 
                    mt: "auto", // Push buttons to bottom
                    pt: 4
                  };
                  
                  return (
                    <Wrapper key={`btns-wrapper-${sectionIndex}`} {...wrapperProps}>
                      {section.cta.map((ctaItem, btnIndex) => (
                        <ButtonComponent
                          key={`cta-${btnIndex}`}
                          action={ctaItem.action}
                          label={ctaItem.label}
                          ariaLabel={ctaItem.ariaLabel}
                          tooltip={ctaItem.tooltip}
                          variant={ctaItem.variant || 'solid'}
                          icons={ctaItem.icons}
                          onClick={() => handleButtonAction(ctaItem.action, ctaItem)}
                          textDecoration={ctaItem.textDecoration}
                          isTouchDevice={isTouchDevice}
                          isMouseDevice={isMouseDevice}
                        />
                      ))}
                    </Wrapper>
                  );
                }
                
                return (
                  <ComponentFactory
                    key={`component-${sectionIndex}`}
                    section={section}
                    theme={liveViewTheme}
                    priorDecisions={priorDecisions}
                    formData={formData}
                    updateFormData={updateFormData}
                    handleButtonAction={handleButtonAction}
                  />
                );
              })}
            </Box>
          </Flex>
        </BackgroundWrapper>
      </Box>
    );
  };

  const handleButtonAction = (action, ctaItem) => {
    if (action === 'openModal') {
      setModalContent(ctaItem.modalContent);
      onOpen();
    }
  };

  const handleTreeNodeClick = (node) => {
    if (node.screen) {
      setSelectedItem(node.screen);
    }
  };

  return (
    <Box height="100vh" pt={14}>
      <PageViewTracker pageName={"TemplatePage"} />
      <Flex height="100%" gap={4}>
        <VStack
          align="stretch"
          width="50%"
          spacing={4}
          p={4}
          borderRight="1px solid var(--border-color)"
        >
          <Flex justify="space-between" align="center" pb={4}>
            <Text fontWeight="bold" fontSize="xl">
              Input JSON
            </Text>
            <IconButton
              aria-label="Toggle live view theme"
              icon={liveViewTheme === 'light' ? <MoonIcon /> : <SunIcon />}
              onClick={() =>
                setLiveViewTheme((prev) =>
                  prev === 'light' ? 'dark' : 'light'
                )
              }
              size="sm"
            />
          </Flex>
          <Textarea
            placeholder="Paste JSON here"
            rows={25}
            value={jsonInput}
            onChange={(e) => handleJsonChange(e.target.value)}
            aria-label="JSON input area"
          />
          {error && (
            <Text color="red.500" fontSize="sm" aria-live="polite">
              {error}
            </Text>
          )}

                     <VStack align="stretch" spacing={2}>
             <Text fontWeight="bold" fontSize="md">
               View Options
             </Text>
             <Box 
               border="1px solid" 
               borderColor="gray.200" 
               borderRadius="md" 
               p={2}
               maxH="300px"
               overflowY="auto"
             >
               {parsedContent && parsedContent.length > 0 ? (
                 <CustomTreeView 
                   data={convertToTreeStructure(parsedContent)} 
                   onNodeClick={handleTreeNodeClick}
                 />
               ) : (
                 <Text color="gray.500" textAlign="center" py={4}>
                   No views available
                 </Text>
               )}
             </Box>
           </VStack>

          <Box mt={4} pt={4} borderTop="1px solid var(--border-color)">
            <Text fontWeight="bold" fontSize="xl" mb={4}>
              Save to Database
            </Text>
            <VStack spacing={3}>
              <FormControl>
                <FormLabel>Program Prefix</FormLabel>
                <Input
                  value={programPrefix}
                  onChange={(e) => setProgramPrefix(e.target.value)}
                  placeholder="e.g., TASKREFERENCEID"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Task Type</FormLabel>
                <Input
                  value={taskType}
                  onChange={(e) => setTaskType(e.target.value)}
                  placeholder="e.g., PROGRAMINTRO"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Revision</FormLabel>
                <Input
                  value={revision}
                  onChange={(e) => setRevision(e.target.value)}
                  placeholder="e.g., 1.0"
                />
              </FormControl>
              <Button
                colorScheme="teal"
                width="100%"
                onClick={handleSave}
                isLoading={isSaving}
                loadingText="Saving..."
                isDisabled={!parsedContent || isSaving}
              >
                Save to Database
              </Button>
            </VStack>
          </Box>
        </VStack>

                 <Box
           width="50%"
           height="100%"
           p={4}
           bg="gray.100"
           overflow="auto"
           position="relative"
         >
           <Text fontSize="lg" fontWeight="bold" mb={2}>
             Interactive Flowchart
           </Text>
                       <Box height="calc(100% - 32px)" width="100%" p={4}>
              <InteractiveMermaid mermaidCode={mermaidCode} />
            </Box>
         </Box>
      </Flex>
      
             {/* Content Preview Modal */}
       <Modal 
         isOpen={selectedItem !== null} 
         onClose={() => setSelectedItem(null)} 
         size="full"
         closeOnOverlayClick={true}
         closeOnEsc={true}
       >
         <ModalOverlay />
                   <ModalContent 
            width="90vw" 
            height="90vh" 
            maxWidth="390px" 
            maxHeight="90vh"
            margin="auto"
            my={6}
            mx={4}
          >

            <ModalBody p={0} height="100%" overflow="hidden">
              {selectedItem && renderContent(selectedItem)}
            </ModalBody>
          </ModalContent>
       </Modal>

       {/* Confirmation Modal */}
       <Modal isOpen={isOpen} onClose={onClose} size="xl">
         <ModalOverlay />
         <ModalContent>
           <ModalHeader>Confirmation</ModalHeader>
           <ModalCloseButton />
           <ModalBody>
             {modalContent && (
               <ModalComponent
                 modalContent={modalContent}
                 handleButtonAction={handleButtonAction}
               />
             )}
           </ModalBody>
         </ModalContent>
       </Modal>

      {isSaving && (
        <Flex
          position="fixed"
          top="0"
          left="0"
          width="100vw"
          height="100vh"
          bg="rgba(0, 0, 0, 0.5)"
          justify="center"
          align="center"
          zIndex="overlay"
        >
          <Spinner
            size="xl"
            thickness="4px"
            color="teal.500"
          />
        </Flex>
      )}
    </Box>
  );
};

export default Template;